import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import Modal from '../components/Modal';
import { getSorBrands, getSorBatches, getSorItems, sorMarkPaid } from '../api/sor';
import { downloadHtml } from '../utils/download';
import { brandPageHtml, recordsPageHtml } from '../utils/sorAdvicePages';

const todayStr = () => new Date().toISOString().split('T')[0];
const money = (v) => (v == null ? '—' : `₹${Number(v).toFixed(2)}`);

// Settlement advice CSV, matching the ZOCS Brand Settlement Advice template.
const ddmmyyyy = (iso) => { if (!iso) return ''; const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${y}`; };
const csvCell = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
function downloadAdviceCsv(a) {
  const amt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lines = [
    ['ZOCS Brand Settlement Advice'],
    ['Brand', a.brand],
    ['Date of Export', ddmmyyyy(a.export_date)],
    ['Date of Last Payment', ddmmyyyy(a.last_payment_date)],
    [],
    ['Brand', 'Shipment Date', 'Amount to Pay', 'TDS Deducted', 'Advice Ref'],
    ...a.rows.map((r) => [a.brand, ddmmyyyy(r.shipment_date), amt(r.amount), '', a.reference]),
  ];
  const blob = new Blob([lines.map((l) => l.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const el = document.createElement('a');
  el.href = url; el.download = `${a.reference}.csv`; el.click();
  URL.revokeObjectURL(url);
}

export default function SOR() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [batches, setBatches] = useState([]);
  const [selBatches, setSelBatches] = useState(new Set());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Payment flow
  const [payMode, setPayMode] = useState(false);
  const [selPay, setSelPay] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [payDate, setPayDate] = useState(todayStr());
  const [paying, setPaying] = useState(false);

  useEffect(() => { getSorBrands().then(setBrands).catch(() => {}); }, []);

  function cancelPay() { setPayMode(false); setSelPay(new Set()); }

  // Brand change -> load its batches, all selected.
  useEffect(() => {
    if (!brandId) { setBatches([]); setSelBatches(new Set()); setItems([]); return; }
    getSorBatches(brandId).then((bs) => {
      setBatches(bs);
      setSelBatches(new Set(bs)); // all batches open by default
    }).catch(() => {});
  }, [brandId]);

  const loadItems = useCallback(async () => {
    if (!brandId || selBatches.size === 0) { setItems([]); return; }
    setLoading(true);
    try { setItems(await getSorItems(brandId, Array.from(selBatches))); }
    catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, [brandId, selBatches]);
  useEffect(() => { loadItems(); }, [loadItems]);

  function toggleBatch(b) {
    setSelBatches((prev) => { const n = new Set(prev); n.has(b) ? n.delete(b) : n.add(b); return n; });
  }
  function togglePay(id) {
    setSelPay((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  // One table per SOR start date (latest first).
  const groups = useMemo(() => {
    const m = {};
    items.forEach((it) => { (m[it.sor_start_date] = m[it.sor_start_date] || []).push(it); });
    return Object.entries(m).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  }, [items]);

  const payItems = useMemo(() => items.filter((it) => selPay.has(it.item_id)), [items, selPay]);
  const totalExcl = payItems.reduce((s, it) => s + (it.cost_price || 0), 0);
  const totalIncl = payItems.reduce((s, it) => s + (it.cost_price_with_tax || 0), 0);

  // "Select all unpaid" = every sold, not-yet-paid piece across ALL batches.
  // Fetches all batches itself (rather than relying on the current, possibly
  // filtered list) and opens every batch so the selection is visible.
  async function selectAllUnpaid() {
    const all = await getSorItems(brandId, batches);
    setSelBatches(new Set(batches));
    setSelPay(new Set(all.filter(it => it.status === 'sold' && !it.paid_to_brand).map(it => it.item_id)));
  }

  async function confirmPay() {
    setPaying(true);
    try {
      const res = await sorMarkPaid(Array.from(selPay), payDate);
      if (res.advice) {
        downloadAdviceCsv(res.advice);
        const paidIds = new Set(res.paid_item_ids || []);
        const date = res.advice.export_date.split('-').reverse().join('-');
        const paid = (res.shipment_items || []).filter((it) => paidIds.has(it.item_id));
        downloadHtml(brandPageHtml(res.advice, paid), `For ${res.advice.brand} - ${date}.html`);
        downloadHtml(recordsPageHtml(res.advice, res.shipment_items || [], res.paid_item_ids || [], res.advices),
          `For Skipzy Records ${res.advice.brand} - ${date}.html`);
      }
      if (res.skipped_already_paid) alert(`${res.skipped_already_paid} piece(s) were already paid and were skipped.`);
      setShowConfirm(false); cancelPay();
      loadItems();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to mark paid'); }
    finally { setPaying(false); }
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <h1 className="text-2xl font-black text-gray-800 mb-1">SOR</h1>
      <p className="text-sm text-gray-400 mb-5">Sale-or-Return performance &amp; brand payments</p>

      {/* Filters + pay controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select value={brandId} onChange={(e) => { setBrandId(e.target.value); cancelPay(); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-zoddle-green">
          <option value="">Select brand…</option>
          {brands.map((b) => <option key={b.brand_partner_id} value={b.brand_partner_id}>{b.name}</option>)}
        </select>
        {batches.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">SOR batches:</span>
            {batches.map((b) => (
              <button key={b} onClick={() => toggleBatch(b)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${selBatches.has(b) ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                {b}
              </button>
            ))}
          </div>
        )}
        {brandId && items.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            {!payMode ? (
              <button onClick={() => { setPayMode(true); setSelPay(new Set()); }}
                className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90">Pay</button>
            ) : (
              <>
                <span className="text-xs text-gray-400">Tick rows to pay</span>
                <button onClick={selectAllUnpaid} disabled={loading}
                  className="border border-zoddle-green text-zoddle-green text-sm font-semibold px-4 py-2 rounded-lg hover:bg-green-50 disabled:opacity-50">Select all unpaid</button>
                <button onClick={() => { setPayDate(todayStr()); setShowConfirm(true); }} disabled={selPay.size === 0}
                  className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">Proceed ({selPay.size})</button>
                <button onClick={cancelPay}
                  className="border border-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      ) : !brandId ? (
        <div className="text-center py-16 text-gray-400 text-sm">Select a brand to begin</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No SOR items for the selected batches</div>
      ) : groups.map(([startDate, rows]) => (
        <div key={startDate} className="mb-8">
          <h2 className="text-sm font-bold text-gray-700 mb-2">
            SOR batch — {startDate} <span className="text-gray-400 font-normal">({rows.length} items)</span>
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
                  {payMode && <th className="px-2 py-2"></th>}
                  <th className="px-2 py-2 text-left">Photo</th>
                  <th className="px-2 py-2 text-left">Item ID</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-right">Cost +tax</th>
                  <th className="px-2 py-2 text-right">Cost</th>
                  <th className="px-2 py-2 text-right">MRP</th>
                  <th className="px-2 py-2 text-right">Zoddle</th>
                  <th className="px-2 py-2 text-right">Sold price</th>
                  <th className="px-2 py-2 text-left">Sold date</th>
                  <th className="px-2 py-2 text-left">Order ID</th>
                  <th className="px-2 py-2 text-right" title="Times this design was sent to customers">Sendouts</th>
                  <th className="px-2 py-2 text-right" title="Times this design was sold">Sales</th>
                  <th className="px-2 py-2 text-left">SOR start</th>
                  <th className="px-2 py-2 text-left">SOR end</th>
                  <th className="px-2 py-2 text-left">Paid?</th>
                  <th className="px-2 py-2 text-left">Paid on</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <tr key={it.item_id} className={`border-b border-gray-100 hover:bg-gray-50 ${selPay.has(it.item_id) ? 'bg-green-50' : ''}`}>
                    {payMode && (
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={selPay.has(it.item_id)} onChange={() => togglePay(it.item_id)} />
                      </td>
                    )}
                    <td className="px-2 py-1.5">
                      {it.image_url_1
                        ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-9 h-9 rounded object-cover bg-gray-100" loading="lazy" />
                        : <div className="w-9 h-9 rounded bg-gray-100" />}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-gray-600">{it.zoddle_barcode}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.status}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.cost_price_with_tax)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.cost_price)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.mrp)}</td>
                    <td className="px-2 py-1.5 text-right">{money(it.zoddle_price)}</td>
                    <td className="px-2 py-1.5 text-right">{it.sold_price != null ? money(it.sold_price) : '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sold_date || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sold_order_id || '—'}</td>
                    <td className="px-2 py-1.5 text-right">{it.design_sendouts}</td>
                    <td className="px-2 py-1.5 text-right">{it.design_sales}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sor_start_date || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.sor_end_date || '—'}</td>
                    <td className="px-2 py-1.5">{it.paid_to_brand ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-gray-400">No</span>}</td>
                    <td className="px-2 py-1.5 text-gray-600">{it.paid_to_brand_at || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {showConfirm && (
        <Modal title="Confirm SOR payment" onClose={() => setShowConfirm(false)} wide>
          <p className="text-sm text-gray-600 mb-3">
            Marking <span className="font-semibold">{payItems.length}</span> piece{payItems.length !== 1 ? 's' : ''} as paid to the brand.
          </p>
          <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-lg mb-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-500">
                  <th className="px-3 py-1.5 text-left">Item ID</th>
                  <th className="px-3 py-1.5 text-left">Design</th>
                  <th className="px-3 py-1.5 text-right">Cost</th>
                  <th className="px-3 py-1.5 text-right">Cost +tax</th>
                </tr>
              </thead>
              <tbody>
                {payItems.map((it) => (
                  <tr key={it.item_id} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 font-mono text-gray-600">{it.zoddle_barcode}</td>
                    <td className="px-3 py-1.5 text-gray-600 truncate max-w-[12rem]">{it.design_name || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{money(it.cost_price)}</td>
                    <td className="px-3 py-1.5 text-right">{money(it.cost_price_with_tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-500">Total (excl. GST)</span><span className="font-semibold">{money(totalExcl)}</span>
          </div>
          <div className="flex justify-between text-sm mb-4">
            <span className="text-gray-500">Total (incl. GST)</span><span className="font-bold text-gray-800">{money(totalIncl)}</span>
          </div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Payment date</label>
          <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-zoddle-green" />
          <div className="flex gap-3">
            <button onClick={confirmPay} disabled={paying || !payDate}
              className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50">
              {paying ? 'Saving…' : 'Confirm'}
            </button>
            <button onClick={() => setShowConfirm(false)}
              className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
